import React, { useEffect, useRef, useState } from "react";
import { MCard } from "components/Shared";
import MSelect, { MSelectOption } from "components/Shared/MSelect";
import { Button } from "mueller-controls";
import Grid from "@mui/material/Grid";
import {getBillingBase, getBillingMonths} from "requests/customerInvoicing";
import { BillingBaseFile } from "interfaces/Mueller.Business.Models";
import xlsx from "xlsx";
import LoadingCover from "components/Shared/LoadingCover";

const monthsOfTheYearOptions = [
  "December",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
].map((m, i) => ({ value: i, label: m }));

const renameKey = (obj: object, curKey: string, newKey: string) => {
  if (curKey !== newKey && obj.hasOwnProperty(curKey)) {
    obj[newKey] = obj[curKey];
    delete obj[curKey];
  }
  return obj;
};

type MonthOption = {
  value: string; // store ISO date string (e.g., "2025-02-01")
  label: string; // display text (e.g., "February 2025")
};

// this control is set up to allow them to pick months and years, but then we got rid of all of that and decided to just go with a current dropdown button
// i left the code in because the business changes their mind all the time
const CustomerInvoicing = () => {
  const [dates, setDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    }
  }, []);

  // fetch available months from API
  useEffect(() => {
    getBillingMonths((data) => {
      if (mountedRef.current) {
        setDates(data);
        if (data.length > 0) {
          setSelectedDate(data[0]); // default to first available date
        }
      }
    }, mountedRef);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      getBillingBase(selectedDate, (billingFile) => {
        console.log("Billing data:", billingFile);
        // handle billing file logic
      }, mountedRef);
    }
  }, [selectedDate]);

  const getInvoicingData = () => {
    if (!selectedDate) return;

    setLoadingMessage("Getting billing data...");
    // pass the selected month (date string) into your API
    getBillingBase(selectedDate, handleInvoicingData, mountedRef);
  };

  const handleInvoicingData = (billingFile: BillingBaseFile) => {
    setLoadingMessage("Building Spreadsheet...");

    setTimeout(() => {
      let wb = xlsx.utils.book_new();

      billingFile.data.forEach(obj => {
        billingFile.columnDefinitions.forEach(({ key, name }) => {
          renameKey(obj, key, name);
        });
      });

      let ws = xlsx.utils.json_to_sheet(billingFile.data, {
        header: billingFile.columnDefinitions.map(cd => cd.name),
      });

      xlsx.utils.book_append_sheet(wb, ws, "Sheet 1");
      xlsx.writeFile(wb, billingFile.fileName);
      setLoadingMessage(null);
    });
  };

  return (
      <div style={{ position: "relative", height: "100%", padding: "8px" }}>
        {loadingMessage && <LoadingCover loadingMessages={[loadingMessage]} />}
        <Grid container spacing={2}>
          <Grid size={4}>
            <MCard title="Create Invoice File">
            <MSelect
                    placeholder="Select Date"
                    options={dates.map(d => {
                      const dt = new Date(d);
                      const label = dt.toLocaleString("default", { month: "long", year: "numeric" });
                      return { value: d, label };
                    })}
                    onChange={date => setSelectedDate(date?.value ?? null)}
                    value={selectedDate ? {
                      value: selectedDate,
                      label: new Date(selectedDate).toLocaleString("default", { month: "long", year: "numeric" })
                    } : null}
            />
            <Button
                    disabled={!selectedDate}
                    onClick={getInvoicingData}
            >
              {selectedDate
                      ? `Download Invoice for ${new Date(selectedDate).toLocaleString("default", { month: "long", year: "numeric" })}`
                      : "Download Invoice"}
            </Button>
            </MCard>
          </Grid>
        </Grid>
      </div>
  );

};

export default CustomerInvoicing;
